import { useSession } from "next-auth/react";
import { useContext, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import CustomToast from "components/atoms/CustomToast";
import { TYPO } from "styles/Typo";
import Button from "components/common/buttons/buttons";
import { useParams } from "next/navigation";
import * as Sentry from "@sentry/nextjs";
import { DetailButtonsContext } from "../../Header/Buttons/Index";
import useIsDetailLoading from "states/detailLoading";
import { useStockAlarm } from "lib/analytics/react/hooks";
import { toLoginPage } from "utils/routing";

function DetailAlarmRegister({
  init,
  setInit,
  score,
  isAlarmRegistered,
  setAlarmSuccessToastVisible,
  detailData,
}: {
  init: boolean;
  setInit: (init: boolean) => void;
  score: number;
  isAlarmRegistered: boolean;
  setAlarmSuccessToastVisible: (visible: boolean) => void;
  detailData: any;
}) {
  const router = useRouter();
  const { t } = useTranslation("detail");

  const isDetailLoading = useIsDetailLoading();
  const [heightAnimation, setHeightAnimation] = useState(false);
  const [height, setHeight] = useState(0);
  const { ticker } = useParams();
  const stockAlarm = useStockAlarm();

  const { alarmRegister, session, sessionUpdate } =
    useContext(DetailButtonsContext);

  const isLoginUser = session?.user?.email || false;

  useEffect(() => {
    if (isDetailLoading) return;
    if (!init) {
      setHeightAnimation(true);
    }
  }, [init, isDetailLoading]);

  useEffect(() => {
    if (heightAnimation) {
      const viewDetailAlarmCTA = localStorage.getItem("viewDetailAlarmCTA");
      if (viewDetailAlarmCTA) {
        if (
          new Date().getTime() - new Date(viewDetailAlarmCTA).getTime() >
          1000 * 60 * 60 * 24
        )
          localStorage.removeItem("viewDetailAlarmCTA");
        else return;
      }

      setTimeout(() => {
        setHeight(100);

        setTimeout(() => {
          setInit(true);
          localStorage.setItem("viewDetailAlarmCTA", new Date().toISOString());
        }, 600);
      }, 850);
    }
  }, [heightAnimation]);
  const getMaxHeight = () => {
    let value: string = "auto";
    if (typeof window !== "undefined") {
      value =
        localStorage.getItem("viewDetailAlarmCTA") &&
        new Date().getTime() -
          new Date(localStorage.getItem("viewDetailAlarmCTA") || "").getTime() <
          1000 * 60 * 60 * 24
          ? "auto"
          : !heightAnimation
          ? init
            ? "auto"
            : "0px"
          : `${height}px`;
    }
    return value;
  };
  // 알림 등록
  const registerAlarm = async () => {
    if (typeof window !== "undefined") {
      stockAlarm.start({
        ticker: detailData?.ITEM_CD_DL,
        market: detailData?.LOC === "kr" ? "kr" : "us",
        from_section_id: "stock_banner_attractiveness_notify",
      });
      window.dataLayer.push({
        event: "cDetailAlarm",
      });
    }
    if (!isLoginUser) {
      notLoginUserCallback();
      return;
    }

    try {
      await alarmRegister({
        from: "cta",
        setAlarmSuccessToastVisible: setAlarmSuccessToastVisible,
        success: stockAlarm.success,
        fail: stockAlarm.fail,
      });
      await sessionUpdate();
      // setAlarmToastVisible(true);
    } catch (e: any) {
      Sentry.captureException(e);
      console.log(e);
    }
    // 알림 등록 후 삭제
    const isRegistered = localStorage.getItem(`detailAlarmRegister:${ticker}`);
    if (isRegistered) {
      localStorage.removeItem(`detailAlarmRegister:${ticker}`);
    }
  };
  const notLoginUserCallback = () => {
    // 비로그인 유저 -> 로그인 후 자동 알림 등록
    localStorage.setItem(
      `detailAlarmRegister:${ticker}`,
      new Date().toISOString()
    );
    toLoginPage(router, "stock_alarm");
    // router.push(`/login?from=${router.asPath}`);
  };

  useEffect(() => {
    // 비로그인 유저 -> 로그인 후 자동 알림 등록
    if (isLoginUser) {
      const isRegistered = localStorage.getItem(
        `detailAlarmRegister:${ticker}`
      );
      // 1시간 이상 지났으면 삭제

      if (
        isRegistered &&
        new Date().getTime() - new Date(isRegistered).getTime() > 1000 * 60 * 60
      ) {
        localStorage.removeItem(`detailAlarmRegister:${ticker}`);
        return;
      }

      if (isRegistered && !isAlarmRegistered) {
        // 알림 자동 등록
        console.log("!! 알림 자동 등록 isRegistered", isRegistered);
        registerAlarm();
      }
    }
  }, [isLoginUser]);

  if (isDetailLoading) return null;

  return (
    <>
      <div
        className="w-full overflow-hidden"
        style={{
          transition: `max-height 0.6s ease-in-out`,
          maxHeight: getMaxHeight() || "0px",
          transformOrigin: "top",
        }}
      >
        <div className="flex px-6 py-4 justify-between items-center bg-blue5">
          <div className="flex flex-col">
            <p className={`${TYPO.TYPO7_1_RG} text-blue40`}>
              {t("currentAttractivenessScore", { score })}
            </p>
            <p className={`${TYPO.TYPO5_1_MD} text-gray80`}>
              {t("notifyWhenScoreRises")}
            </p>
          </div>
          <Button
            type={"primary"}
            size={"md"}
            text={t("notifyMe")}
            clickHandler={registerAlarm}
            dataGtmId={"sDetail-alarm-register-button"}
          />
        </div>
      </div>
    </>
  );
}

export default DetailAlarmRegister;
